import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import {
  Warning as WarningIcon,
  LocalShipping as SupplyIcon,
  Science as ProductIcon,
  Group as GroupIcon,
  DeleteForever as DeleteIcon,
  Person as PersonIcon
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const ClientDeleteDialog = ({ open, onClose, clientId }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: 影響確認, 2: 最終確認
  const [impactData, setImpactData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 削除影響調査
  useEffect(() => {
    if (open && clientId) {
      setStep(1);
      fetchDeletionImpact();
    }
  }, [open, clientId]);

  const fetchDeletionImpact = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(`/api/clients/${clientId}/deletion-impact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImpactData(response.data);
    } catch (err) {
      setImpactData({
        clientName: "不明",
        productCount: 0,
        supplyCount: 0,
        groupCount: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFirstStepNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = sessionStorage.getItem("token");
      await axios.delete(`/api/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 削除完了メッセージ
      const deletedItems = [];
      if (impactData.productCount > 0) deletedItems.push(`製品設定${impactData.productCount}件`);
      if (impactData.supplyCount > 0) deletedItems.push(`供給データ${impactData.supplyCount}件`);
      if (impactData.groupCount > 0) deletedItems.push(`グループ関連${impactData.groupCount}件`);

      const message = deletedItems.length > 0
        ? `顧客「${impactData.clientName}」と関連データ（${deletedItems.join('、')}）を削除しました`
        : `顧客「${impactData.clientName}」を削除しました`;

      alert(message);
      navigate("/clientList");
    } catch (err) {
      if (err.response?.status === 404) {
        alert("この顧客は既に削除されています。");
        navigate("/clientList");
      } else {
        alert("削除に失敗しました。もう一度お試しください。");
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (!deleting && !loading) {
      setStep(1);
      onClose();
    }
  };

  const hasDeletionImpact = impactData && (impactData.productCount > 0 || impactData.supplyCount > 0 || impactData.groupCount > 0);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      {step === 1 ? (
        // 第1段階：削除影響の確認
        <>
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            color: 'warning.main' 
          }}>
            <WarningIcon />
            顧客削除の影響確認
          </DialogTitle>

          <DialogContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>削除影響を調査中...</Typography>
              </Box>
            ) : impactData ? (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  以下の顧客を削除しようとしています：
                </Typography>

                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="h6" color="text.primary">
                      {impactData.clientName}
                    </Typography>
                  </Box>
                </Box>

                {hasDeletionImpact ? (
                  <>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>⚠️ 以下の関連データも同時に削除されます：</strong>
                      </Typography>
                    </Alert>

                    <List dense sx={{ mb: 2 }}>
                      {impactData.productCount > 0 && (
                        <ListItem>
                          <ListItemIcon>
                            <ProductIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`製品設定: ${impactData.productCount}件`}
                            secondary="この顧客の全ての製品設定データ"
                          />
                        </ListItem>
                      )}
                      
                      {impactData.supplyCount > 0 && (
                        <ListItem>
                          <ListItemIcon>
                            <SupplyIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`供給履歴: ${impactData.supplyCount}件`}
                            secondary="過去の全ての供給記録と履歴データ"
                          />
                        </ListItem>
                      )}
                      
                      {impactData.groupCount > 0 && (
                        <ListItem>
                          <ListItemIcon>
                            <GroupIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`グループ関連: ${impactData.groupCount}件`}
                            secondary="グループへの所属情報"
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    この顧客には関連データがありません。顧客情報のみが削除されます。
                  </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary">
                  この操作は完全に元に戻すことができません。続行しますか？
                </Typography>
              </>
            ) : null}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleClose} 
              disabled={loading}
              variant="outlined"
            >
              キャンセル
            </Button>
            <Button 
              onClick={handleFirstStepNext}
              disabled={loading}
              variant="contained"
              color="warning"
            >
              削除の詳細を確認
            </Button>
          </DialogActions>
        </>
      ) : (
        // 第2段階：最終確認（テキスト入力なし）
        <>
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            color: 'error.main' 
          }}>
            <DeleteIcon />
            最終確認 - 削除実行
          </DialogTitle>

          <DialogContent>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>🚨 重要：以下が実行されます</strong>
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>顧客「{impactData.clientName}」の完全削除</li>
                {impactData.productCount > 0 && <li>製品設定 {impactData.productCount}件の削除</li>}
                {impactData.supplyCount > 0 && <li>供給履歴 {impactData.supplyCount}件の削除</li>}
                {impactData.groupCount > 0 && <li>グループ関連 {impactData.groupCount}件の削除</li>}
                <li><strong>この操作は絶対に元に戻せません</strong></li>
              </ul>
            </Alert>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ p: 2, bgcolor: 'rgba(244, 67, 54, 0.05)', borderRadius: 1 }}>
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 'medium' }}>
                削除を実行する場合は、下の「完全に削除する」ボタンをクリックしてください。
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              削除後は顧客一覧画面に戻ります。
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleBack} 
              disabled={deleting}
            >
              戻る
            </Button>
            <Button 
              onClick={handleClose} 
              disabled={deleting}
              variant="outlined"
            >
              キャンセル
            </Button>
            <Button 
              onClick={handleDelete}
              disabled={deleting}
              variant="contained"
              color="error"
              startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {deleting ? "削除中..." : "完全に削除する"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ClientDeleteDialog;